import { useMemo } from 'react';

import { PathNode } from '../ConversationPathConfigurator';

export function useFilteredPaths(paths: string[], query: string) {
  return useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) {
      return paths;
    }
    return paths.filter((path) => path.toLowerCase().includes(normalized));
  }, [paths, query]);
}

function appendPath(
  parentPath: string,
  segment: string,
  segmentType: 'object' | 'array'
) {
  if (!parentPath) {
    return segment;
  }
  return segmentType === 'array'
    ? `${parentPath}${segment}`
    : `${parentPath}.${segment}`;
}
/**
 * Sorts PathNode objects with array indices before object properties,
 * and array indices in numerical order.
 */
export function sortPathNodes(a: PathNode, b: PathNode): number {
  const aIsArray = a.name.startsWith('[');
  const bIsArray = b.name.startsWith('[');

  if (aIsArray && bIsArray) {
    return Number(a.name.slice(1, -1)) - Number(b.name.slice(1, -1));
  }

  return aIsArray ? -1 : bIsArray ? 1 : a.name.localeCompare(b.name);
}

function buildPathTreeFromValues(value: unknown): PathNode[] {
  const buildNode = (val: unknown, name: string, path: string): PathNode => {
    const node: PathNode = { name, path, children: [] };

    if (val == null || typeof val !== 'object') {
      node.value = val;
      return node;
    }

    if (Array.isArray(val)) {
      node.children = val.map((entry, index) => {
        const segment = `[${index}]`;
        return buildNode(entry, segment, appendPath(path, segment, 'array'));
      });
    } else {
      node.children = Object.entries(val as Record<string, unknown>)
        .map(([key, child]) =>
          buildNode(child, key, appendPath(path, key, 'object'))
        )
        .sort(sortPathNodes);
    }

    return node;
  };

  return buildNode(value, '', '').children;
}
export function usePathTreeFromValues(values: unknown) {
  return useMemo(() => buildPathTreeFromValues(values), [values]);
}

/**
 * Converts negative array indices in paths to their positive equivalents by traversing the actual object.
 * For example: 'data.items[0].messages[-1].content' becomes 'data.items[0].messages[2].content' in a 3-item messages array.
 */
export function interpolateNegativeIndices(
  paths: string[],
  values: unknown
): string[] {
  return paths.map((path) => interpolatePath(path, values));
}

/**
 * Interpolates a single path by traversing the object and converting negative indices.
 */
function interpolatePath(path: string, values: unknown): string {
  // Split path into segments: 'data.items[0].messages[-1].content' -> ['data.items', '[0]', 'messages', '[-1]', 'content']
  const segments = path
    .split(/(\[[^\]]+\])/)
    .filter((segment) => segment !== '');
  const interpolatedSegments: string[] = [];
  let currentValue = values;

  for (const segment of segments) {
    if (segment.startsWith('[') && segment.endsWith(']')) {
      // This is an array index segment
      const indexStr = segment.slice(1, -1);
      const index = parseInt(indexStr, 10);

      if (index < 0) {
        // Negative index - need to interpolate
        if (!Array.isArray(currentValue)) {
          // Can't interpolate if current value isn't an array
          interpolatedSegments.push(segment);
        } else {
          // Convert negative index to positive
          const positiveIndex = currentValue.length + index;
          interpolatedSegments.push(`[${positiveIndex}]`);
          currentValue = currentValue[positiveIndex];
        }
      } else {
        // Positive index - use as-is
        interpolatedSegments.push(segment);
        if (Array.isArray(currentValue)) {
          currentValue = currentValue[index];
        }
      }
    } else {
      // This is an object property segment (could contain dots)
      interpolatedSegments.push(segment);

      // Navigate through the object property
      if (
        currentValue != null &&
        typeof currentValue === 'object' &&
        !Array.isArray(currentValue)
      ) {
        // Handle dotted property names like 'data.items'
        const propertyPath = segment.split('.');
        for (const prop of propertyPath) {
          if (prop) {
            // Skip empty strings from leading/trailing dots
            currentValue = (currentValue as Record<string, unknown>)[prop];
            if (currentValue === undefined) break;
          }
        }
      }
    }
  }

  return interpolatedSegments.join('');
}

export function getConvertedNegativePath(path: string, node: PathNode) {
  const ownPath = node.path;
  const childSuffix = path.slice(ownPath.length);

  // Check if childSuffix starts with an array index (e.g., "[0]", "[1]", etc.)
  const arrayIndexMatch = childSuffix.match(/^\[(\d+)\]/);
  if (arrayIndexMatch) {
    const arrayIndex = parseInt(arrayIndexMatch[1], 10);
    // if array index is the last item and not the first item convert to negative
    if (arrayIndex === node.children.length - 1 && arrayIndex !== 0) {
      const negativeIndex = arrayIndex - node.children.length;
      const updatedPath =
        ownPath +
        `[${negativeIndex}]` +
        childSuffix.slice(arrayIndexMatch[0].length);
      return updatedPath;
    }
  }

  return path;
}
